import React from 'react';
import { FlaskConicalIcon, TrendingUpIcon, ZapIcon } from 'lucide-react';
interface StudyCard {
  icon: React.ReactNode;
  title: string;
  description: string;
  source: string;
}
const studies: StudyCard[] = [{
  icon: <FlaskConicalIcon className="w-8 h-8 text-brand-red" aria-hidden="true" data-id="element-215" />,
  title: 'Clinical Efficacy Study',
  description: 'A 24-week study showed 39% increase in hair density among participants using 650nm red light therapy consistently.',
  source: 'Journal of Dermatological Science, 2019'
}, {
  icon: <TrendingUpIcon className="w-8 h-8 text-brand-red" aria-hidden="true" data-id="element-216" />,
  title: 'Hair Growth Acceleration',
  description: 'Red light therapy at 650nm wavelength stimulates ATP production in hair follicle cells, accelerating the anagen (growth) phase.',
  source: 'International Journal of Trichology, 2020'
}, {
  icon: <ZapIcon className="w-8 h-8 text-brand-red" aria-hidden="true" data-id="element-217" />,
  title: 'Cellular Activation',
  description: 'Photobiomodulation increases blood flow to the scalp by up to 54%, delivering essential nutrients to dormant follicles.',
  source: 'Lasers in Medical Science, 2021'
}];
export function ScienceSection() {
  return <section className="w-full bg-gray-50 py-20 px-4" data-id="element-218">
      <div className="max-w-7xl mx-auto" data-id="element-219">
        {/* Section Header */}
        <div className="text-center mb-16" data-id="element-220">
          <h2 className="text-4xl md:text-5xl font-extrabold text-brand-black mb-4" data-id="element-221">
            Backed by Clinical Research
          </h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto" data-id="element-222">
            Red light therapy isn't just marketing—it's proven science with
            decades of peer-reviewed research
          </p>
        </div>

        {/* Study Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16" data-id="element-223">
          {studies.map((study, index) => <div key={index} className="bg-white rounded-xl shadow-lg p-8 border border-gray-200" data-id="element-224">
              <div className="bg-red-50 rounded-lg p-4 inline-flex mb-6" data-id="element-225">
                {study.icon}
              </div>
              <h3 className="text-xl font-bold text-brand-black mb-4" data-id="element-226">
                {study.title}
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4" data-id="element-227">
                {study.description}
              </p>
              <p className="text-sm text-gray-500 italic" data-id="element-228">{study.source}</p>
            </div>)}
        </div>

        {/* Science Explanation */}
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12" data-id="element-229">
          <h3 className="text-3xl font-bold text-brand-black mb-6 text-center" data-id="element-230">
            How Photobiomodulation Works
          </h3>
          <div className="max-w-4xl mx-auto" data-id="element-231">
            <p className="text-lg text-gray-700 leading-relaxed mb-6" data-id="element-232">
              Photobiomodulation (PBM), also known as low-level light therapy
              (LLLT), uses specific wavelengths of red light to stimulate
              cellular activity in hair follicles. The 650nm wavelength
              penetrates the scalp and triggers a cascade of biological
              responses:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-233">
              <div className="text-center" data-id="element-234">
                <div className="bg-brand-red text-white rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-3 font-bold text-xl" data-id="element-235">
                  1
                </div>
                <h4 className="font-bold text-brand-black mb-2" data-id="element-236">
                  Cellular Energy
                </h4>
                <p className="text-gray-600 text-sm" data-id="element-237">
                  Increases ATP production in mitochondria, providing energy for
                  hair growth
                </p>
              </div>
              <div className="text-center" data-id="element-238">
                <div className="bg-brand-red text-white rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-3 font-bold text-xl" data-id="element-239">
                  2
                </div>
                <h4 className="font-bold text-brand-black mb-2" data-id="element-240">Blood Flow</h4>
                <p className="text-gray-600 text-sm" data-id="element-241">
                  Improves scalp circulation, delivering oxygen and nutrients to
                  follicles
                </p>
              </div>
              <div className="text-center" data-id="element-242">
                <div className="bg-brand-red text-white rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-3 font-bold text-xl" data-id="element-243">
                  3
                </div>
                <h4 className="font-bold text-brand-black mb-2" data-id="element-244">
                  Follicle Activation
                </h4>
                <p className="text-gray-600 text-sm" data-id="element-245">
                  Reawakens dormant follicles and extends the growth phase of
                  existing hair
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}